<section
  class="ch-vessel-detail"
  [class.ch-vessel-detail--dark]="isDarkMode"
  [class.ch-light]="!isDarkMode"
  [class.ch-vessel-detail--embedded]="embedded"
>
  <!-- Optional Header if standalone -->
  <div class="module-detail-header" *ngIf="!embedded">
    <div class="header-text-group">
      <p class="module-detail-eyebrow">
        <mat-icon class="eyebrow-icon">local_fire_department</mat-icon>
        Cargo Heating Module
      </p>
      <h2 class="module-detail-title">{{ resolvedVesselName }} — Onboarding Details</h2>
    </div>
    <div class="module-detail-actions">
      <button type="button" class="btn back-btn" (click)="back.emit()">
        <mat-icon>arrow_back</mat-icon>
        Back to Database
      </button>
    </div>
  </div>

  <!-- Loading State -->
  <div class="module-detail-loading" *ngIf="loading">
    <mat-spinner diameter="36"></mat-spinner>
    <span>Loading cargo heating onboarding parameters…</span>
  </div>

  <!-- Main Content Structure -->
  <div class="ch-dashboard-content" *ngIf="!loading">

    <!-- 1. HERO IDENTITY & METADATA BANNER -->
    <div class="ch-hero-card">
      <div class="ch-hero-main">
        <div class="ch-hero-vessel-info">
          <div class="vessel-badge-icon">
            <mat-icon>directions_boat</mat-icon>
          </div>
          <div class="vessel-identity">
            <h1 class="vessel-name">{{ resolvedVesselName }}</h1>
            <div class="vessel-tags-row">
              <span class="vessel-tag tag-imo">
                <strong>IMO</strong> {{ resolvedImo }}
              </span>
              <span class="vessel-tag tag-segment" *ngIf="segment && segment !== '—'">
                <strong>Segment</strong> {{ segment }}
              </span>
              <span class="vessel-tag tag-type" *ngIf="tankerType && tankerType !== '—'">
                <strong>Type</strong> {{ tankerType }}
              </span>
              <span class="vessel-tag tag-setup" *ngIf="heatingSetup && heatingSetup !== '—'">
                <mat-icon class="mini-icon">whatshot</mat-icon>
                {{ heatingSetup }}
              </span>
            </div>
          </div>
        </div>

        <div class="ch-hero-timestamps" *ngIf="creationTimeFormatted !== '—' || modificationTimeFormatted !== '—'">
          <div class="timestamp-item" *ngIf="creationTimeFormatted !== '—'">
            <span class="timestamp-label">Created:</span>
            <span class="timestamp-val">{{ creationTimeFormatted }}</span>
          </div>
          <div class="timestamp-item" *ngIf="modificationTimeFormatted !== '—'">
            <span class="timestamp-label">Last Modified:</span>
            <span class="timestamp-val">{{ modificationTimeFormatted }}</span>
          </div>
        </div>
      </div>

      <!-- Quick KPI Strip -->
      <div class="ch-kpi-strip">
        <div class="kpi-pill">
          <div class="kpi-icon-box kpi-fire">
            <mat-icon>local_fire_department</mat-icon>
          </div>
          <div class="kpi-body">
            <span class="kpi-label">Heating Setup</span>
            <span class="kpi-value">{{ heatingSetup !== '—' ? heatingSetup : 'Steam Coils' }}</span>
          </div>
        </div>

        <div class="kpi-pill">
          <div class="kpi-icon-box kpi-tanks">
            <mat-icon>grid_view</mat-icon>
          </div>
          <div class="kpi-body">
            <span class="kpi-label">Total Tanks</span>
            <span class="kpi-value">{{ tankCount !== '—' ? tankCount + ' Tanks' : '14 Tanks' }}</span>
          </div>
        </div>

        <div class="kpi-pill">
          <div class="kpi-icon-box" [ngClass]="steamPipesInsulation ? 'kpi-success' : 'kpi-neutral'">
            <mat-icon>{{ steamPipesInsulation ? 'check_circle' : 'do_not_disturb_on' }}</mat-icon>
          </div>
          <div class="kpi-body">
            <span class="kpi-label">Pipes Insulation</span>
            <span class="kpi-value" [class.text-accent]="steamPipesInsulation">
              {{ steamPipesInsulation ? 'Insulated' : 'Not Insulated' }}
            </span>
          </div>
        </div>

        <div class="kpi-pill">
          <div class="kpi-icon-box" [ngClass]="egcsScrubber ? 'kpi-success' : 'kpi-neutral'">
            <mat-icon>{{ egcsScrubber ? 'verified' : 'remove_circle_outline' }}</mat-icon>
          </div>
          <div class="kpi-body">
            <span class="kpi-label">EGCS Scrubber</span>
            <span class="kpi-value" [class.text-accent]="egcsScrubber">
              {{ egcsScrubber ? 'Fitted' : 'Not Fitted' }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- UPLOADED ONBOARDING DOCUMENTS (view only) -->
    <div class="ch-card ch-card--documents">
      <div class="ch-card__header">
        <div class="ch-card__title-group">
          <mat-icon class="card-title-icon">folder_open</mat-icon>
          <div>
            <h3 class="ch-card__title">Uploaded Documents</h3>
            <p class="ch-card__subtitle">Files attached while onboarding — click one to view it</p>
          </div>
        </div>
      </div>
      <div class="ch-card__body">
        <div class="ch-documents-grid">
          <app-ch-section-document *ngFor="let doc of onboardingDocuments" [readonly]="true" [imo]="resolvedImo"
            [docType]="doc.docType" [label]="doc.label"></app-ch-section-document>
        </div>
      </div>
    </div>

    <!-- 2. BOILER SPECIFICATIONS & RECOVERY SYSTEMS (GRID) -->
    <div class="ch-grid-row ch-grid-row--boilers">

      <!-- Main Boiler Card -->
      <div class="ch-card ch-card--main-boiler">
        <div class="ch-card__header">
          <div class="ch-card__title-group">
            <mat-icon class="card-title-icon fire-icon">whatshot</mat-icon>
            <div>
              <h3 class="ch-card__title">Main Boiler System</h3>
              <p class="ch-card__subtitle" *ngIf="mainBoiler?.boiler_manufacturer">
                {{ mainBoiler?.boiler_manufacturer }}
                <span class="badge-units" *ngIf="mainBoiler?.boiler_count">({{ mainBoiler?.boiler_count }} Units)</span>
              </p>
            </div>
          </div>
        </div>

        <div class="ch-card__body">
          <div class="spec-metrics-grid">
            <div class="spec-metric-box">
              <span class="metric-label">Boiler Capacity</span>
              <div class="metric-val-group">
                <span class="metric-number">{{ formatNumber(mainBoiler?.boiler_capacity_kg_h, 0) }}</span>
                <span class="metric-unit">kg/h</span>
              </div>
            </div>

            <div class="spec-metric-box highlight-box">
              <span class="metric-label">Steam Prod. @ Max Eff.</span>
              <div class="metric-val-group">
                <span class="metric-number">{{ formatNumber(mainBoiler?.steam_prod_at_max_efficiency_kg_h, 0) }}</span>
                <span class="metric-unit">kg/h</span>
              </div>
            </div>

            <div class="spec-metric-box">
              <span class="metric-label">Aux Boiler Fuel Cons.</span>
              <div class="metric-val-group">
                <span class="metric-number">{{ formatNumber(mainBoiler?.aux_boiler_fuel_consumption_kg_h, 0) }}</span>
                <span class="metric-unit">kg/h</span>
              </div>
            </div>

            <div class="spec-metric-box">
              <span class="metric-label">Peak Efficiency</span>
              <div class="metric-val-group">
                <span class="metric-number text-accent">{{ peakEfficiency > 0 ? (peakEfficiency | number:'1.2-2') : '—' }}</span>
                <span class="metric-unit">%</span>
              </div>
            </div>

            <div class="spec-metric-box">
              <span class="metric-label">Boiler Load</span>
              <div class="metric-val-group">
                <span class="metric-number">{{ formatNumber(mainBoiler?.boiler_load_pct, 0) }}</span>
                <span class="metric-unit">%</span>
              </div>
            </div>

            <div class="spec-metric-box">
              <span class="metric-label">Deck Steam Pressure</span>
              <div class="metric-val-group">
                <span class="metric-number">{{ formatNumber(mainBoiler?.deck_steam_pressure_kg_cm2, 1) }}</span>
                <span class="metric-unit">kg/cm²</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Composite Boiler & Economizer (EGB) Card -->
      <div class="ch-card ch-card--composite">
        <div class="ch-card__header">
          <div class="ch-card__title-group">
            <mat-icon class="card-title-icon energy-icon">bolt</mat-icon>
            <div>
              <h3 class="ch-card__title">Composite Boiler & Economizer (EGB)</h3>
              <p class="ch-card__subtitle">Auxiliary Heat Recovery & Steam Generation</p>
            </div>
          </div>
        </div>

        <div class="ch-card__body composite-split-body">
          <!-- Composite Boiler Section -->
          <div class="composite-subgroup">
            <h4 class="subgroup-title">
              <mat-icon class="subgroup-icon">settings_input_component</mat-icon>
              Composite Boiler
            </h4>
            <div class="subgroup-metrics-grid">
              <div class="subgroup-item">
                <span class="subgroup-label">Capacity</span>
                <span class="subgroup-val">{{ formatNumber(compositeBoiler?.composite_boiler_capacity_kg_h, 0) }} kg/h</span>
              </div>
              <div class="subgroup-item">
                <span class="subgroup-label">Fuel Consumption</span>
                <span class="subgroup-val">{{ formatNumber(compositeBoiler?.composite_boiler_fuel_consumption_kg_h, 0) }} kg/h</span>
              </div>
              <div class="subgroup-item">
                <span class="subgroup-label">Steam Output (Oil Fired)</span>
                <span class="subgroup-val">{{ formatNumber(compositeBoiler?.steam_output_oil_fired_kg_h, 0) }} kg/h</span>
              </div>
              <div class="subgroup-item">
                <span class="subgroup-label">Steam Output (ME Exhaust)</span>
                <span class="subgroup-val">{{ formatNumber(compositeBoiler?.steam_output_me_exhaust_kg_h, 0) }} kg/h</span>
              </div>
              <div class="subgroup-item">
                <span class="subgroup-label">Steam Output (AE Exhaust)</span>
                <span class="subgroup-val">{{ formatNumber(compositeBoiler?.steam_output_ae_exhaust_kg_h, 0) }} kg/h</span>
              </div>
              <div class="subgroup-item">
                <span class="subgroup-label">Deck Heater Capacity</span>
                <span class="subgroup-val">{{ formatNumber(compositeBoiler?.deck_heater_capacity_kw, 0) }} kW</span>
              </div>
              <div class="subgroup-item">
                <span class="subgroup-label">AE Add. Consumption</span>
                <span class="subgroup-val">{{ formatNumber(compositeBoiler?.ae_additional_consumption_mt, 1) }} MT</span>
              </div>
            </div>
          </div>

          <!-- EGB / Economizer Section -->
          <div class="composite-subgroup egb-subgroup">
            <h4 class="subgroup-title">
              <mat-icon class="subgroup-icon">air</mat-icon>
              EGB / Economizer
            </h4>
            <div class="subgroup-metrics-grid">
              <div class="subgroup-item">
                <span class="subgroup-label">Steam Output Capacity</span>
                <span class="subgroup-val">{{ formatNumber(egbEconomizer?.steam_output_capacity_kg_h, 0) }} kg/h</span>
              </div>
              <div class="subgroup-item">
                <span class="subgroup-label">Exhaust Gas Flow Rate</span>
                <span class="subgroup-val">{{ formatNumber(egbEconomizer?.exhaust_gas_flow_rate_kg_h, 0) }} kg/h</span>
              </div>
              <div class="subgroup-item">
                <span class="subgroup-label">Gas Temp (Inlet)</span>
                <span class="subgroup-val">{{ formatNumber(egbEconomizer?.inlet_gas_temperature_c, 1) }} °C</span>
              </div>
              <div class="subgroup-item">
                <span class="subgroup-label">Gas Temp (Outlet)</span>
                <span class="subgroup-val">{{ formatNumber(egbEconomizer?.outlet_gas_temperature_c, 1) }} °C</span>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- 3. BOILER THERMAL EFFICIENCY CURVE -->
    <div class="ch-card ch-card--efficiency" *ngIf="efficiencyPoints.length > 0">
      <div class="ch-card__header ch-card__header--with-actions">
        <div class="ch-card__title-group">
          <mat-icon class="card-title-icon chart-icon">show_chart</mat-icon>
          <div>
            <h3 class="ch-card__title">Boiler Thermal Efficiency Curve</h3>
            <p class="ch-card__subtitle">Efficiency Performance Profile Across Steam Production Loads</p>
          </div>
        </div>

        <div class="curve-stats-strip">
          <div class="curve-stat-chip chip-peak" *ngIf="peakEfficiencyPoint">
            <span class="chip-label">Peak Efficiency:</span>
            <span class="chip-value">{{ peakEfficiencyPoint.thermalEfficiency }}% @ {{ formatNumber(peakEfficiencyPoint.steamProduction, 0) }} kg/h</span>
          </div>
          <div class="curve-stat-chip chip-range">
            <span class="chip-label">Range:</span>
            <span class="chip-value">{{ formatNumber(minSteamProduction, 0) }} – {{ formatNumber(maxSteamProduction, 0) }} kg/h</span>
          </div>
          <div class="curve-stat-chip chip-count">
            <span class="chip-label">Points:</span>
            <span class="chip-value">{{ efficiencyPoints.length }}</span>
          </div>

          <!-- View Toggle -->
          <div class="curve-view-toggle">
            <button
              type="button"
              class="toggle-btn"
              [class.active]="activeCurveView === 'chart'"
              (click)="setCurveView('chart')"
              title="Show Graph View"
            >
              <mat-icon>insights</mat-icon>
              Chart
            </button>
            <button
              type="button"
              class="toggle-btn"
              [class.active]="activeCurveView === 'table'"
              (click)="setCurveView('table')"
              title="Show Data Points View"
            >
              <mat-icon>dataset</mat-icon>
              Data Points ({{ efficiencyPoints.length }})
            </button>
          </div>
        </div>
      </div>

      <div class="ch-card__body">
        <!-- Interactive Highcharts Chart -->
        <div class="chart-wrapper" [style.display]="activeCurveView === 'chart' ? 'block' : 'none'">
          <div *ngIf="boilerEfficiencyChart" [chart]="boilerEfficiencyChart" class="highcharts-host"></div>
        </div>

        <!-- Revamped Data Points View -->
        <div class="ch-points-explorer" [style.display]="activeCurveView === 'table' ? 'flex' : 'none'">
          
          <!-- Peak Efficiency Spotlight Banner -->
          <div class="peak-spotlight-banner" *ngIf="peakEfficiencyPoint">
            <div class="spotlight-left">
              <div class="spotlight-trophy-box">
                <mat-icon class="trophy-icon">workspace_premium</mat-icon>
              </div>
              <div class="spotlight-text">
                <div class="spotlight-badge">MAXIMUM THERMAL EFFICIENCY</div>
                <h4 class="spotlight-title">
                  {{ peakEfficiencyPoint.thermalEfficiency }}% Peak Thermal Efficiency
                </h4>
                <p class="spotlight-desc">
                  Attained at <strong>{{ formatNumber(peakEfficiencyPoint.steamProduction, 1) }} kg/h</strong> steam output (Point #{{ peakEfficiencyPoint.index ?? 4 }}).
                </p>
              </div>
            </div>

            <div class="spotlight-stats-row">
              <div class="spot-stat">
                <span class="spot-label">Optimal Load</span>
                <span class="spot-val">{{ formatNumber(peakEfficiencyPoint.steamProduction, 0) }} <small>kg/h</small></span>
              </div>
              <div class="spot-divider"></div>
              <div class="spot-stat">
                <span class="spot-label">Mean Efficiency</span>
                <span class="spot-val">{{ avgEfficiency | number:'1.2-2' }}%</span>
              </div>
              <div class="spot-divider"></div>
              <div class="spot-stat">
                <span class="spot-label">Base Efficiency</span>
                <span class="spot-val">{{ formatNumber(minEfficiencyPoint?.thermalEfficiency, 2) }}%</span>
              </div>
            </div>
          </div>

          <!-- Points Filter & Search Toolbar -->
          <div class="points-toolbar">
            <!-- Load Range Filter Tabs -->
            <div class="load-filter-tabs">
              <button
                type="button"
                class="tab-filter-btn"
                [class.active]="selectedEfficiencyRange === 'all'"
                (click)="selectedEfficiencyRange = 'all'"
              >
                All ({{ efficiencyPoints.length }})
              </button>
              <button
                type="button"
                class="tab-filter-btn"
                [class.active]="selectedEfficiencyRange === 'low'"
                (click)="selectedEfficiencyRange = 'low'"
              >
                Low Load (9k–15k)
              </button>
              <button
                type="button"
                class="tab-filter-btn"
                [class.active]="selectedEfficiencyRange === 'mid'"
                (click)="selectedEfficiencyRange = 'mid'"
              >
                Mid Load (15k–25k)
              </button>
              <button
                type="button"
                class="tab-filter-btn"
                [class.active]="selectedEfficiencyRange === 'high'"
                (click)="selectedEfficiencyRange = 'high'"
              >
                High Load (25k–35k)
              </button>
              <button
                type="button"
                class="tab-filter-btn"
                [class.active]="selectedEfficiencyRange === 'milestones'"
                (click)="selectedEfficiencyRange = 'milestones'"
              >
                <mat-icon class="mini-btn-icon">flag</mat-icon>
                Milestones
              </button>
            </div>

            <!-- Search Input & Layout Switcher -->
            <div class="toolbar-actions-right">
              <div class="points-search-box">
                <mat-icon class="search-icon">search</mat-icon>
                <input
                  type="text"
                  class="search-input"
                  placeholder="Filter by load or %..."
                  [(ngModel)]="efficiencySearchTerm"
                />
                <button
                  *ngIf="efficiencySearchTerm"
                  type="button"
                  class="clear-search-btn"
                  (click)="efficiencySearchTerm = ''"
                >
                  <mat-icon>close</mat-icon>
                </button>
              </div>

              <div class="layout-toggle-pills">
                <button
                  type="button"
                  class="layout-btn"
                  [class.active]="efficiencyViewLayout === 'cards'"
                  (click)="efficiencyViewLayout = 'cards'"
                  title="Grid Cards View"
                >
                  <mat-icon>grid_view</mat-icon>
                </button>
                <button
                  type="button"
                  class="layout-btn"
                  [class.active]="efficiencyViewLayout === 'matrix'"
                  (click)="efficiencyViewLayout = 'matrix'"
                  title="Compact Matrix View"
                >
                  <mat-icon>table_rows</mat-icon>
                </button>
              </div>
            </div>
          </div>

          <!-- OPTION 1: POINT CARDS GRID VIEW (DEFAULT) -->
          <div class="points-grid-container scroll-bar" *ngIf="efficiencyViewLayout === 'cards'">
            <div
              class="point-metric-card"
              *ngFor="let pt of filteredEfficiencyPoints; trackBy: trackByIndex"
              [class.is-peak]="pt.thermalEfficiency === peakEfficiency"
            >
              <div class="point-card-top">
                <div class="point-index-pill">
                  <span class="pt-number">#{{ pt.index < 10 ? '0' + pt.index : pt.index }}</span>
                  <span class="peak-gold-badge" *ngIf="pt.thermalEfficiency === peakEfficiency">
                    <mat-icon class="mini-gold-icon">star</mat-icon>
                    PEAK
                  </span>
                </div>
                <span
                  class="eff-rating-badge"
                  [ngClass]="getEfficiencyCategory(pt.thermalEfficiency).class"
                >
                  <mat-icon class="mini-cat-icon">{{ getEfficiencyCategory(pt.thermalEfficiency).icon }}</mat-icon>
                  {{ getEfficiencyCategory(pt.thermalEfficiency).label }}
                </span>
              </div>

              <div class="point-card-middle">
                <div class="steam-output-chip">
                  <mat-icon class="steam-icon">cloud</mat-icon>
                  <span class="steam-val">{{ formatNumber(pt.steamProduction, 1) }}</span>
                  <span class="steam-unit">kg/h</span>
                </div>
                <div class="eff-output-number" [class.text-accent]="pt.thermalEfficiency === peakEfficiency">
                  <strong>{{ pt.thermalEfficiency | number:'1.2-2' }}</strong>
                  <small>%</small>
                </div>
              </div>

              <div class="point-card-bottom">
                <div class="eff-bar-track">
                  <div
                    class="eff-bar-fill"
                    [ngClass]="pt.thermalEfficiency >= 88 ? 'bar-peak' : (pt.thermalEfficiency >= 85 ? 'bar-high' : 'bar-mid')"
                    [style.width.%]="getEfficiencyProgressWidth(pt.thermalEfficiency)"
                  ></div>
                </div>
              </div>
            </div>

            <!-- Empty search state -->
            <div class="points-empty-search" *ngIf="filteredEfficiencyPoints.length === 0">
              <mat-icon class="empty-icon">search_off</mat-icon>
              <p>No efficiency data points matching "{{ efficiencySearchTerm }}"</p>
              <button type="button" class="btn-reset-filter" (click)="efficiencySearchTerm = ''; selectedEfficiencyRange = 'all'">
                Reset Filters
              </button>
            </div>
          </div>

          <!-- OPTION 2: COMPACT SLEEK MATRIX VIEW -->
          <div class="matrix-table-container scroll-bar" *ngIf="efficiencyViewLayout === 'matrix'">
            <table class="ch-ultra-table ch-points-matrix-table">
              <thead>
                <tr>
                  <th class="col-index">#</th>
                  <th>Steam Production</th>
                  <th class="text-right">Thermal Efficiency</th>
                  <th>Operating Category</th>
                  <th style="min-width: 140px;">Efficiency Spectrum</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  *ngFor="let pt of filteredEfficiencyPoints; trackBy: trackByIndex"
                  [class.is-peak-row]="pt.thermalEfficiency === peakEfficiency"
                >
                  <td class="col-index font-bold">#{{ pt.index < 10 ? '0' + pt.index : pt.index }}</td>
                  <td class="td-steam-load font-bold">
                    <span class="steam-load-tag">
                      <mat-icon class="mini-steam-icon">cloud</mat-icon>
                      {{ formatNumber(pt.steamProduction, 1) }} kg/h
                    </span>
                  </td>
                  <td class="text-right numeric-data font-bold" [class.peak-text]="pt.thermalEfficiency === peakEfficiency">
                    <span class="eff-highlight-num">{{ pt.thermalEfficiency | number:'1.2-2' }}%</span>
                    <span class="peak-pill-tag" *ngIf="pt.thermalEfficiency === peakEfficiency">★ PEAK</span>
                  </td>
                  <td>
                    <span class="eff-rating-pill" [ngClass]="getEfficiencyCategory(pt.thermalEfficiency).class">
                      {{ getEfficiencyCategory(pt.thermalEfficiency).label }}
                    </span>
                  </td>
                  <td>
                    <div class="matrix-meter-track">
                      <div
                        class="matrix-meter-fill"
                        [ngClass]="pt.thermalEfficiency >= 88 ? 'bar-peak' : (pt.thermalEfficiency >= 85 ? 'bar-high' : 'bar-mid')"
                        [style.width.%]="getEfficiencyProgressWidth(pt.thermalEfficiency)"
                      ></div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

        </div>
      </div>
    </div>

    <!-- 4. STEAM PIPELINE CONFIGURATION -->
    <div class="ch-card ch-card--pipelines">
      <div class="ch-card__header">
        <div class="ch-card__title-group">
          <mat-icon class="card-title-icon pipe-icon">alt_route</mat-icon>
          <div>
            <h3 class="ch-card__title">Steam Pipeline Configuration</h3>
            <p class="ch-card__subtitle">Engine Room, Main Deck Distribution & Branch Piping Architecture</p>
          </div>
        </div>

        <div class="pipeline-totals-strip">
          <div class="pipe-stat-badge">
            <span class="stat-label">Main Line Total:</span>
            <span class="stat-val">{{ formatNumber(totalMainLineLength, 2) }} m</span>
          </div>
          <div class="pipe-stat-badge">
            <span class="stat-label">Branch Lines Total:</span>
            <span class="stat-val">{{ formatNumber(totalBranchLineLength, 1) }} m ({{ totalBranchLineCount }} Lines)</span>
          </div>
        </div>
      </div>

      <div class="ch-card__body">
        <!-- Engine Room Line Hero Banner -->
        <div class="engine-room-banner" *ngIf="engineRoomLine">
          <div class="er-badge-title">
            <div class="er-icon-box">
              <mat-icon class="er-icon">precision_manufacturing</mat-icon>
            </div>
            <div>
              <span class="er-main-title">Engine Room Steam Feed Line</span>
              <span class="er-sub-title">High Pressure Heavy Steam Supply</span>
            </div>
          </div>
          <div class="er-specs-row">
            <div class="er-spec-item">
              <span class="er-label">Inside Diameter</span>
              <span class="er-value">{{ formatNumber(engineRoomLine.inside_diameter_mm, 1) }} <small>mm</small></span>
            </div>
            <div class="er-spec-item">
              <span class="er-label">Outside Diameter</span>
              <span class="er-value">{{ formatNumber(engineRoomLine.outside_diameter_mm, 1) }} <small>mm</small></span>
            </div>
            <div class="er-spec-item highlight">
              <span class="er-label">Piping Length</span>
              <span class="er-value">{{ formatNumber(engineRoomLine.length_m, 2) }} <small>m</small></span>
            </div>
          </div>
        </div>

        <!-- Pipelines Interactive Layout -->
        <div class="pipelines-grid">
          
          <!-- Main Deck Distribution Flow -->
          <div class="pipeline-section-box" *ngIf="mainLineSegments.length > 0">
            <div class="section-box-header">
              <div class="box-header-title">
                <mat-icon class="box-icon">linear_scale</mat-icon>
                <span>Main Deck Distribution Segments</span>
              </div>
              <span class="box-header-badge">{{ mainLineSegments.length }} Zones</span>
            </div>

            <div class="pipeline-cards-list">
              <div class="pipe-flow-card" *ngFor="let seg of mainLineSegments; trackBy: trackByIndex">
                <div class="flow-card-header">
                  <span class="flow-zone-badge">{{ seg.segmentKey }}</span>
                  <span class="flow-length-tag">
                    <mat-icon class="mini-pipe-icon">straighten</mat-icon>
                    {{ formatNumber(seg.length_m, 2) }} m
                  </span>
                </div>
                <div class="flow-card-metrics">
                  <div class="flow-metric">
                    <span class="metric-caption">Inside Ø</span>
                    <span class="metric-data">{{ formatNumber(seg.inside_diameter_mm, 1) }} mm</span>
                  </div>
                  <div class="flow-metric-divider"></div>
                  <div class="flow-metric">
                    <span class="metric-caption">Outside Ø</span>
                    <span class="metric-data">{{ formatNumber(seg.outside_diameter_mm, 1) }} mm</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="box-footer-summary">
              <span>Cumulative Main Line Length</span>
              <strong>{{ formatNumber(totalMainLineLength, 2) }} m</strong>
            </div>
          </div>

          <!-- Branch Distribution Network -->
          <div class="pipeline-section-box" *ngIf="branchLineGroups.length > 0">
            <div class="section-box-header">
              <div class="box-header-title">
                <mat-icon class="box-icon">call_split</mat-icon>
                <span>Branch Line Distribution Groups</span>
              </div>
              <span class="box-header-badge">{{ totalBranchLineCount }} Total Lines</span>
            </div>

            <div class="pipeline-cards-list">
              <div class="pipe-flow-card branch-flow-card" *ngFor="let branch of branchLineGroups; let idx = index; trackBy: trackByIndex">
                <div class="flow-card-header">
                  <span class="flow-zone-badge branch-badge">Group {{ idx + 1 }} ({{ branch.line_count }} Lines)</span>
                  <span class="flow-length-tag">
                    <mat-icon class="mini-pipe-icon">straighten</mat-icon>
                    {{ formatNumber(branch.total_length_m, 1) }} m
                  </span>
                </div>
                <div class="flow-card-metrics">
                  <div class="flow-metric">
                    <span class="metric-caption">Inside Ø</span>
                    <span class="metric-data">{{ formatNumber(branch.inside_diameter_mm, 1) }} mm</span>
                  </div>
                  <div class="flow-metric-divider"></div>
                  <div class="flow-metric">
                    <span class="metric-caption">Outside Ø</span>
                    <span class="metric-data">{{ formatNumber(branch.outside_diameter_mm, 1) }} mm</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="box-footer-summary">
              <span>Cumulative Branch Piping Length</span>
              <strong>{{ formatNumber(totalBranchLineLength, 1) }} m</strong>
            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- 5. TANK HEATING SPECIFICATIONS (98% VOL) -->
    <div class="ch-card ch-card--heating-tanks" *ngIf="heatingTableRows.length > 0">
      <div class="ch-card__header ch-card__header--with-actions">
        <div class="ch-card__title-group">
          <mat-icon class="card-title-icon tank-icon">whatshot</mat-icon>
          <div>
            <h3 class="ch-card__title">Tank Heating Specifications (98% Volume)</h3>
            <p class="ch-card__subtitle">Coil Surface Ratios, Heating Coil Lengths & Thermal Group Configurations</p>
          </div>
        </div>

        <div class="header-action-group">
          <div class="view-mode-toggle">
            <button
              type="button"
              class="view-toggle-btn"
              [class.active]="heatingTableViewMode === 'cards'"
              (click)="heatingTableViewMode = 'cards'"
              title="Card Grid View"
            >
              <mat-icon>grid_view</mat-icon>
              Cards
            </button>
            <button
              type="button"
              class="view-toggle-btn"
              [class.active]="heatingTableViewMode === 'table'"
              (click)="heatingTableViewMode = 'table'"
              title="Data Table View"
            >
              <mat-icon>table_rows</mat-icon>
              Matrix
            </button>
          </div>
          <span class="ch-count-badge">{{ heatingTableRows.length }} Tanks Configured</span>
        </div>
      </div>

      <div class="ch-card__body">

        <!-- CARDS VIEW (DEFAULT - STATE OF THE ART) -->
        <div class="tank-cards-grid" *ngIf="heatingTableViewMode === 'cards'">
          <div
            class="tank-card"
            *ngFor="let tank of heatingTableRows; trackBy: trackByIndex"
            [ngClass]="getTankPositionClass(tank.tankKey)"
          >
            <!-- Tank Card Header -->
            <div class="tank-card-header">
              <div class="tank-identity-pill">
                <span class="pos-badge" [ngClass]="getTankPositionBadge(tank.tankKey).class">
                  <mat-icon class="pos-icon">{{ getTankPositionBadge(tank.tankKey).icon }}</mat-icon>
                  {{ getTankPositionBadge(tank.tankKey).label }}
                </span>
                <span class="tank-key-name">{{ tank.tankKey }}</span>
              </div>

              <div class="tank-vol-chip">
                <span class="vol-label">98% Vol:</span>
                <span class="vol-num">{{ formatNumber(tank.tank_volume_98_m3, 1) }}</span>
                <span class="vol-unit">m³</span>
              </div>
            </div>

            <!-- Tank Card Main Metrics -->
            <div class="tank-card-body">
              <div class="tank-heat-ratio-banner">
                <div class="heat-ratio-left">
                  <mat-icon class="fire-flame-icon">local_fire_department</mat-icon>
                  <span class="ratio-title">Heating Ratio</span>
                </div>
                <div class="heat-ratio-val">
                  <strong>{{ formatNumber(tank.heating_ratio_m2_m3, 2) }}</strong>
                  <small>m²/m³</small>
                </div>
              </div>

              <!-- Coil Length Comparison & Progress -->
              <div class="coil-length-section">
                <div class="coil-labels-row">
                  <span class="coil-lbl">Coil Length: Actual vs Required</span>
                  <span class="coil-val-highlight">
                    <b>{{ formatNumber(tank.heating_coil_actual_length_m, 1) }}m</b>
                    <span class="coil-req-txt"> / {{ formatNumber(tank.heating_coil_required_length_m, 1) }}m req</span>
                  </span>
                </div>
                <div class="coil-progress-track">
                  <div
                    class="coil-progress-bar"
                    [style.width.%]="getCoilCoveragePct(tank.heating_coil_actual_length_m, tank.heating_coil_required_length_m)"
                  ></div>
                </div>
              </div>

              <!-- Groups Info Row -->
              <div class="tank-groups-row">
                <div class="group-metric-item">
                  <span class="g-label">Group Layout</span>
                  <span class="g-value">{{ tank.no_of_groups || 0 }} Groups</span>
                </div>
                <div class="group-metric-divider"></div>
                <div class="group-metric-item">
                  <span class="g-label">Length / Group</span>
                  <span class="g-value">{{ formatNumber(tank.length_per_group_m, 1) }} m</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- MATRIX / TABLE VIEW (SMART NAUTICAL MATRIX) -->
        <div class="smart-table-wrapper" *ngIf="heatingTableViewMode === 'table'">
          <div class="table-scroll-surface scroll-bar">
            <table class="ch-smart-table">
              <thead>
                <tr>
                  <th class="th-tank">Tank Identifier</th>
                  <th class="th-pos">Position</th>
                  <th class="text-right">98% Vol</th>
                  <th class="text-center" style="min-width: 170px;">Coil Configuration</th>
                  <th class="text-right">Group Length</th>
                  <th class="text-center">Groups</th>
                  <th class="text-right">Heating Ratio</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  *ngFor="let tank of heatingTableRows; trackBy: trackByIndex"
                  class="smart-table-row"
                  [ngClass]="getTankPositionClass(tank.tankKey)"
                >
                  <td class="td-tank-cell">
                    <div class="tank-cell-content">
                      <span class="pos-dot"></span>
                      <mat-icon class="row-tank-icon">view_in_ar</mat-icon>
                      <span class="tank-name-label">{{ tank.tankKey }}</span>
                    </div>
                  </td>
                  <td>
                    <span class="pos-pill-tag" [ngClass]="getTankPositionBadge(tank.tankKey).class">
                      <mat-icon class="mini-pos-icon">{{ getTankPositionBadge(tank.tankKey).icon }}</mat-icon>
                      {{ getTankPositionBadge(tank.tankKey).label }}
                    </span>
                  </td>
                  <td class="text-right">
                    <span class="numeric-val font-bold">{{ formatNumber(tank.tank_volume_98_m3, 1) }}</span>
                    <span class="unit-tag">m³</span>
                  </td>
                  <td>
                    <div class="coil-spec-cell">
                      <div class="coil-metrics-dual">
                        <span class="coil-actual-chip">
                          <small>Act:</small>
                          <b>{{ formatNumber(tank.heating_coil_actual_length_m, 1) }}m</b>
                        </span>
                        <span class="coil-sep">/</span>
                        <span class="coil-req-chip">
                          <small>Req:</small>
                          <span>{{ formatNumber(tank.heating_coil_required_length_m, 1) }}m</span>
                        </span>
                      </div>
                      <div class="mini-coil-bar-track">
                        <div
                          class="mini-coil-bar-fill"
                          [style.width.%]="getCoilCoveragePct(tank.heating_coil_actual_length_m, tank.heating_coil_required_length_m)"
                        ></div>
                      </div>
                    </div>
                  </td>
                  <td class="text-right">
                    <span class="numeric-val">{{ formatNumber(tank.length_per_group_m, 1) }}</span>
                    <span class="unit-tag">m</span>
                  </td>
                  <td class="text-center">
                    <span class="group-count-badge">{{ tank.no_of_groups || '—' }}</span>
                  </td>
                  <td class="text-right">
                    <div class="heating-ratio-pill">
                      <mat-icon class="mini-fire-icon">local_fire_department</mat-icon>
                      <span class="ratio-val">{{ formatNumber(tank.heating_ratio_m2_m3, 2) }}</span>
                      <small class="ratio-unit">m²/m³</small>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Smart Summary Footer Strip -->
          <div class="smart-table-summary-bar">
            <div class="summary-caption">
              <mat-icon class="summary-icon">analytics</mat-icon>
              <span>Tank Heating Summary</span>
            </div>
            <div class="summary-chips-row">
              <div class="sum-chip">
                <span class="sum-label">Total 98% Vol</span>
                <span class="sum-val accent-text">{{ formatNumber(totalVolume98, 1) }} m³</span>
              </div>
              <div class="sum-divider"></div>
              <div class="sum-chip">
                <span class="sum-label">Avg Heating Ratio</span>
                <span class="sum-val fire-text">{{ formatNumber(avgHeatingRatio, 2) }} m²/m³</span>
              </div>
              <div class="sum-divider"></div>
              <div class="sum-chip">
                <span class="sum-label">Total Coils Fitted</span>
                <span class="sum-val">{{ formatNumber(totalActualCoilLength, 1) }} m</span>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- 6. CAPACITY PLAN & HYDROSTATIC STABILITY -->
    <div class="ch-card ch-card--capacity-plan" *ngIf="capacityPlanRows.length > 0">
      <div class="ch-card__header ch-card__header--with-actions">
        <div class="ch-card__title-group">
          <mat-icon class="card-title-icon stability-icon">architecture</mat-icon>
          <div>
            <h3 class="ch-card__title">Tank Capacity & Hydrostatic Stability Plan (100% Volume)</h3>
            <p class="ch-card__subtitle">Frame Boundaries, Mass Center Coordinates (VCG, LCG, TCG) & Inertia Moments</p>
          </div>
        </div>

        <div class="header-action-group">
          <div class="view-mode-toggle">
            <button
              type="button"
              class="view-toggle-btn"
              [class.active]="capacityViewMode === 'cards'"
              (click)="capacityViewMode = 'cards'"
              title="Card Grid View"
            >
              <mat-icon>grid_view</mat-icon>
              Cards
            </button>
            <button
              type="button"
              class="view-toggle-btn"
              [class.active]="capacityViewMode === 'table'"
              (click)="capacityViewMode = 'table'"
              title="Data Table View"
            >
              <mat-icon>table_rows</mat-icon>
              Matrix
            </button>
          </div>
          <span class="ch-count-badge">{{ capacityPlanRows.length }} Compartments</span>
        </div>
      </div>

      <div class="ch-card__body">

        <!-- CARDS VIEW (DEFAULT - HIGH TECH ARCHITECTURE) -->
        <div class="tank-cards-grid capacity-cards-grid" *ngIf="capacityViewMode === 'cards'">
          <div
            class="tank-card capacity-card"
            *ngFor="let cap of capacityPlanRows; trackBy: trackByIndex"
            [ngClass]="getTankPositionClass(cap.tankKey)"
          >
            <!-- Header -->
            <div class="tank-card-header">
              <div class="tank-identity-pill">
                <span class="pos-badge" [ngClass]="getTankPositionBadge(cap.tankKey).class">
                  <mat-icon class="pos-icon">{{ getTankPositionBadge(cap.tankKey).icon }}</mat-icon>
                  {{ getTankPositionBadge(cap.tankKey).label }}
                </span>
                <span class="tank-key-name">{{ cap.tankKey }}</span>
              </div>

              <div class="frame-range-badge">
                <mat-icon class="mini-frame-icon">straighten</mat-icon>
                <span>FR {{ cap.frame_min !== undefined ? cap.frame_min : '—' }} – {{ cap.frame_max !== undefined ? cap.frame_max : '—' }}</span>
              </div>
            </div>

            <!-- Body -->
            <div class="tank-card-body">
              <!-- Volume & Weight Strip -->
              <div class="capacity-vol-wt-row">
                <div class="cap-metric-box">
                  <span class="cap-lbl">100% Volume</span>
                  <span class="cap-val">{{ formatNumber(cap.volume_100_m3, 1) }} <small>m³</small></span>
                </div>
                <div class="cap-metric-box">
                  <span class="cap-lbl">Weight</span>
                  <span class="cap-val">{{ formatNumber(cap.weight_mt, 1) }} <small>MT</small></span>
                </div>
                <div class="cap-metric-box highlight-purple">
                  <span class="cap-lbl">I Max</span>
                  <span class="cap-val">{{ formatNumber(cap.i_max_m4, 2) }} <small>m⁴</small></span>
                </div>
              </div>

              <!-- Coordinate Axes Grid -->
              <div class="stability-axes-grid">
                <div class="axis-item">
                  <span class="axis-name">VCG <small>(from BL)</small></span>
                  <span class="axis-coord">{{ formatNumber(cap.vcg_from_bl_m, 2) }} m</span>
                </div>
                <div class="axis-item">
                  <span class="axis-name">LCG <small>(from Mid)</small></span>
                  <span class="axis-coord">{{ formatNumber(cap.lcg_from_midship_m, 2) }} m</span>
                </div>
                <div class="axis-item">
                  <span class="axis-name">TCG <small>(from CL)</small></span>
                  <span class="axis-coord">{{ formatNumber(cap.tcg_from_cl_m, 2) }} m</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- MATRIX / TABLE VIEW (SMART HYDROSTATIC MATRIX) -->
        <div class="smart-table-wrapper" *ngIf="capacityViewMode === 'table'">
          <div class="table-scroll-surface scroll-bar">
            <table class="ch-smart-table">
              <thead>
                <tr>
                  <th class="th-tank">Tank Identifier</th>
                  <th class="th-pos">Position</th>
                  <th class="text-center">Frame Range</th>
                  <th class="text-right">100% Vol</th>
                  <th class="text-right">Weight</th>
                  <th class="text-right">VCG (BL)</th>
                  <th class="text-right">LCG (Mid)</th>
                  <th class="text-right">TCG (CL)</th>
                  <th class="text-right">I Max</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  *ngFor="let cap of capacityPlanRows; trackBy: trackByIndex"
                  class="smart-table-row"
                  [ngClass]="getTankPositionClass(cap.tankKey)"
                >
                  <td class="td-tank-cell">
                    <div class="tank-cell-content">
                      <span class="pos-dot"></span>
                      <mat-icon class="row-tank-icon">view_column</mat-icon>
                      <span class="tank-name-label">{{ cap.tankKey }}</span>
                    </div>
                  </td>
                  <td>
                    <span class="pos-pill-tag" [ngClass]="getTankPositionBadge(cap.tankKey).class">
                      <mat-icon class="mini-pos-icon">{{ getTankPositionBadge(cap.tankKey).icon }}</mat-icon>
                      {{ getTankPositionBadge(cap.tankKey).label }}
                    </span>
                  </td>
                  <td class="text-center">
                    <span class="table-frame-chip">
                      FR {{ cap.frame_min !== undefined ? cap.frame_min : '—' }} – {{ cap.frame_max !== undefined ? cap.frame_max : '—' }}
                    </span>
                  </td>
                  <td class="text-right">
                    <span class="numeric-val font-bold">{{ formatNumber(cap.volume_100_m3, 1) }}</span>
                    <span class="unit-tag">m³</span>
                  </td>
                  <td class="text-right">
                    <span class="numeric-val font-bold text-white">{{ formatNumber(cap.weight_mt, 1) }}</span>
                    <span class="unit-tag">MT</span>
                  </td>
                  <td class="text-right">
                    <span class="coord-val">{{ formatNumber(cap.vcg_from_bl_m, 2) }}</span>
                    <span class="unit-tag">m</span>
                  </td>
                  <td class="text-right">
                    <span class="coord-val">{{ formatNumber(cap.lcg_from_midship_m, 2) }}</span>
                    <span class="unit-tag">m</span>
                  </td>
                  <td class="text-right">
                    <span class="coord-val">{{ formatNumber(cap.tcg_from_cl_m, 2) }}</span>
                    <span class="unit-tag">m</span>
                  </td>
                  <td class="text-right">
                    <div class="i-max-pill">
                      <span class="i-max-val">{{ formatNumber(cap.i_max_m4, 2) }}</span>
                      <small class="i-max-unit">m⁴</small>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Smart Summary Footer Strip -->
          <div class="smart-table-summary-bar">
            <div class="summary-caption">
              <mat-icon class="summary-icon">balance</mat-icon>
              <span>Hydrostatic Total Mass</span>
            </div>
            <div class="summary-chips-row">
              <div class="sum-chip">
                <span class="sum-label">Total 100% Volume</span>
                <span class="sum-val accent-text">{{ formatNumber(totalCapacityVolume, 1) }} m³</span>
              </div>
              <div class="sum-divider"></div>
              <div class="sum-chip">
                <span class="sum-label">Total Vessel Weight</span>
                <span class="sum-val text-white">{{ formatNumber(totalCapacityWeight, 1) }} MT</span>
              </div>
              <div class="sum-divider"></div>
              <div class="sum-chip">
                <span class="sum-label">Total Compartments</span>
                <span class="sum-val">{{ capacityPlanRows.length }} Tanks</span>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>

  </div>
</section>
